/** ------------------------------------------------------------------------- *
 * Navigation Dropdowns *
 * -------------------------------------------------------------------------- *
 * @module Dropdown
 * @desc
 * Toggles the display of a dropdown element when clicking the parent link.
 * Settings and initial state are controlled by the attributes on the link
 * element and the state classes on both the link and dropdown elements.
 *
 * ----------------------- *
 * Link Element Attributes *
 * ----------------------- *
 * class="js-dropdown"
 * The selector for initializing the class
 *
 * class="is-open"
 * The state class used for styling purposes (added when toggled)
 *
 * aria-controls="js-targetId"
 * The ID of the target element to be toggled
 *
 * aria-expanded="false"
 * The default state of the toggle elements
 *
 * role="button"
 * Optional: add for non-button elements
 *
 * --------------------------- *
 * Dropdown Element Attributes *
 * --------------------------- *
 * id="js-targetId"
 * The ID matching the aria-controls attribute on the trigger element
 *
 * class="is-open"
 * The state class used for styling purposes (added when toggled)
 */

const settings = {
    // The default DOMString selectors for the module
    selectors: {
        header: '#js-header',
        dropdown: '.js-dropdown',
    },
    // The class to be added to toggled elements
    state_class: 'is-open',
};

const class_arr = [];

/** ------------------ *
 * Close All Dropdowns *
 * ------------------- *
 * @desc Closes any open dropdowns when a click happens outside the header
 */

function closeDropdowns(e) {
    const container_el = document.querySelector(settings.selectors.header);

    // Check if the click happened outside the header
    if (container_el.contains(e.target) === false) {
        // Close any open dropdowns
        class_arr.forEach((dropdown) => {
            if (dropdown.open === true) {
                dropdown.closeDropdown();
            }
        });

        // Remove the unneeded event listener
        window.removeEventListener('click', closeDropdowns);
    }
}

/** ------------------ *
 * Toggle Button Class *
 * ------------------- *
 * @class DropdownLink
 * @desc Creates a clickable link that toggles the display of a dropdown
 */

class DropdownLink {
    /**
     * @constructor
     * @param {Element} el - The HTML element that will trigger the toggle
     */

    constructor(el) {
        // Use attribute values for initial state
        const aria_controls = el.getAttribute('aria-controls');
        const aria_expanded = el.getAttribute('aria-expanded');

        // Object properties
        this.link_el = el;
        this.dropdown_el = document.getElementById(aria_controls);
        this.open = aria_expanded === 'true';

        // Add the link to the class array
        class_arr.push(this);

        // Show/hide content on click
        this.link_el.addEventListener('click', (e) => {
            e.preventDefault();
            this.openDropdown();
        });
    }

    /**
     * @property DropdownLink.openDropdown
     * @method
     * @desc Opens the dropdown
     */

    openDropdown() {
        // Check whether the dropdown is already open
        if (this.open === false) {
            // Update state to opened
            this.link_el.classList.add(settings.state_class);
            this.link_el.setAttribute('aria-expanded', true);
            this.dropdown_el.classList.add(settings.state_class);
            this.open = true;

            // Close any other open dropdowns
            class_arr.forEach((link) => {
                if (link !== this && link.open === true) {
                    link.closeDropdown();
                }
            });

            // Close the dropdown if the user clicks outside the header
            window.addEventListener('click', closeDropdowns);
        } else {
            this.closeDropdown();
        }
    }

    /**
     * @property DropdownLink.closeDropdown
     * @method
     * @desc Closes the dropdown
     */

    closeDropdown() {
        let all_closed = true;

        // Update state to closed
        this.link_el.classList.remove(settings.state_class);
        this.link_el.setAttribute('aria-expanded', false);
        this.dropdown_el.classList.remove(settings.state_class);
        this.open = false;

        class_arr.forEach((dropdown) => {
            if (dropdown.open === true) {
                all_closed = false;
            }
        });

        // Remove event listener if not needed
        if (all_closed === true) {
            window.removeEventListener('click', closeDropdowns);
        }
    }
}

/** ------------ *
 * Module Export *
 * ------------- *
 */

const Dropdown = {
    /**
     * @property Dropdown.selector
     * @desc The default DOMString selector for initializing the module
     */

    selector: settings.selectors.dropdown,

    /**
     * @property Dropdown.init
     * @method
     * @desc Intializes the module
     */

    init() {
        // Convert NodeList to Array
        const buttons = document.querySelectorAll(settings.selectors.dropdown);
        const button_arr = Array.from(buttons);

        // Map the new Class object to the element
        button_arr.forEach((el) => {
            new DropdownLink(el);
        });
    },
};

export default Dropdown;
